import { useEffect, useMemo, useRef, useState } from 'react';
import { calculateFees } from '../lib/feeEngine.js';
import { formatCurrency, formatPercent } from '../lib/formatCurrency.js';
import { buildCalculatedResultsCsv, buildProductTemplateCsv, parseProductCsv } from '../lib/csvUtils.js';
import ProductTable from './ProductTable.jsx';

const summaryDefaults = {
  totalProducts: 0,
  validRows: 0,
  invalidRows: 0,
  totalGrossSales: 0,
  totalMarketplaceFees: 0,
  totalNetPayout: 0,
  totalNetProfit: 0,
  averageMargin: 0,
  lossProducts: 0,
  thinProducts: 0,
};

const STORAGE_KEY = 'marketplaceFeeCalculator.productImport.v1';

const emptyImportState = {
  products: [],
  fileName: '',
  fileWarnings: [],
};

const canUseLocalStorage = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return Boolean(window.localStorage);
  } catch {
    return false;
  }
};

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const safeArray = (value) => (Array.isArray(value) ? value : []);

const restoreProduct = (product, index) => {
  if (!isObject(product) || !isObject(product.input)) {
    return null;
  }

  return {
    rowNumber: Number.isFinite(Number(product.rowNumber)) ? Number(product.rowNumber) : index + 1,
    raw: isObject(product.raw) ? product.raw : {},
    input: product.input,
    warnings: safeArray(product.warnings).map((warning) => String(warning)),
    isValid: Boolean(product.isValid),
    result: isObject(product.result) ? product.result : undefined,
  };
};

const readStoredImportState = () => {
  if (!canUseLocalStorage()) {
    return emptyImportState;
  }

  try {
    const savedValue = window.localStorage.getItem(STORAGE_KEY);

    if (!savedValue) {
      return emptyImportState;
    }

    const parsedValue = JSON.parse(savedValue);

    if (!isObject(parsedValue)) {
      return emptyImportState;
    }

    return {
      products: safeArray(parsedValue.products)
        .map((product, index) => restoreProduct(product, index))
        .filter(Boolean),
      fileName: typeof parsedValue.fileName === 'string' ? parsedValue.fileName : '',
      fileWarnings: safeArray(parsedValue.fileWarnings).map((warning) => String(warning)),
    };
  } catch {
    return emptyImportState;
  }
};

const removeStoredImportState = () => {
  if (!canUseLocalStorage()) {
    return;
  }

  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage failures so the static app remains usable.
  }
};

const writeStoredImportState = (state, summary) => {
  if (!canUseLocalStorage()) {
    return;
  }

  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        version: 1,
        savedAt: new Date().toISOString(),
        ...state,
        summary,
      }),
    );
  } catch {
    // Ignore storage failures so import/export still works without persistence.
  }
};

function SummaryMetric({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-2 text-xl font-bold tracking-normal text-slate-950">{value}</p>
    </div>
  );
}

function createDownload(csvText, filename) {
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export default function ProductImportTool({ rules, onLoadProduct }) {
  const fileInputRef = useRef(null);
  const [importState, setImportState] = useState(readStoredImportState);
  const [isLoading, setIsLoading] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const { products, fileName, fileWarnings } = importState;

  const summary = useMemo(() => {
    if (products.length === 0) {
      return summaryDefaults;
    }

    const validProducts = products.filter((product) => product.isValid && product.result);
    const totals = validProducts.reduce(
      (current, product) => ({
        totalGrossSales: current.totalGrossSales + product.result.grossSales,
        totalMarketplaceFees: current.totalMarketplaceFees + product.result.totalMarketplaceFees,
        totalNetPayout: current.totalNetPayout + product.result.netPayout,
        totalNetProfit: current.totalNetProfit + product.result.netProfit,
        totalMargin: current.totalMargin + product.result.marginPercentage,
        lossProducts: current.lossProducts + (product.result.profitStatus === 'Loss' ? 1 : 0),
        thinProducts: current.thinProducts + (product.result.profitStatus === 'Thin' ? 1 : 0),
      }),
      {
        totalGrossSales: 0,
        totalMarketplaceFees: 0,
        totalNetPayout: 0,
        totalNetProfit: 0,
        totalMargin: 0,
        lossProducts: 0,
        thinProducts: 0,
      },
    );

    return {
      totalProducts: products.length,
      validRows: validProducts.length,
      invalidRows: products.length - validProducts.length,
      totalGrossSales: totals.totalGrossSales,
      totalMarketplaceFees: totals.totalMarketplaceFees,
      totalNetPayout: totals.totalNetPayout,
      totalNetProfit: totals.totalNetProfit,
      averageMargin: validProducts.length > 0 ? totals.totalMargin / validProducts.length : 0,
      lossProducts: totals.lossProducts,
      thinProducts: totals.thinProducts,
    };
  }, [products]);

  useEffect(() => {
    if (products.length === 0 && !fileName && fileWarnings.length === 0) {
      removeStoredImportState();
      return;
    }

    writeStoredImportState(importState, summary);
  }, [importState, summary]);

  const validExportCount = products.length;

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setIsLoading(true);

    try {
      const text = await file.text();
      const parsed = parseProductCsv(text, rules);
      const calculatedProducts = parsed.rows.map((product) =>
        product.isValid
          ? {
              ...product,
              result: calculateFees(product.input, rules),
            }
          : product,
      );

      setImportState({
        products: calculatedProducts,
        fileName: file.name,
        fileWarnings: parsed.fileWarnings,
      });
    } catch (error) {
      setImportState({
        products: [],
        fileName: file.name,
        fileWarnings: [`File tidak bisa dibaca: ${error.message}`],
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setShowClearConfirm(true);
  };

  const confirmClear = () => {
    removeStoredImportState();
    setImportState(emptyImportState);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setShowClearConfirm(false);
  };

  const cancelClear = () => {
    setShowClearConfirm(false);
  };

  const handleExport = () => {
    const csvText = buildCalculatedResultsCsv(products);
    createDownload(csvText, `marketplace-fee-results-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const handleTemplateDownload = () => {
    createDownload(buildProductTemplateCsv(), 'marketplace-fee-template.csv');
  };

  return (
    <section className="panel-card p-5 sm:p-6">
      <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-normal text-slate-950">Input Produk & Import CSV</h2>
          <p className="mt-1 text-sm text-slate-500">Hasil import memakai sample fee rules yang bisa diedit.</p>
          {fileName && <p className="mt-2 text-sm font-semibold text-slate-700">{fileName}</p>}
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={handleTemplateDownload}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
          >
            Download Template CSV
          </button>
          <label className="inline-flex cursor-pointer items-center justify-center rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50">
            Pilih CSV
            <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={handleFileChange} className="sr-only" />
          </label>
          <button
            type="button"
            onClick={handleExport}
            disabled={validExportCount === 0}
            className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            Export Hasil CSV
          </button>
          <button
            type="button"
            onClick={handleClear}
            disabled={products.length === 0 && !fileName}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400 disabled:hover:bg-transparent"
          >
            Bersihkan Produk Impor
          </button>
        </div>
      </div>

      {fileWarnings.length > 0 && (
        <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          {fileWarnings.map((warning) => (
            <p key={warning} className="text-sm leading-6 text-amber-800">
              {warning}
            </p>
          ))}
        </div>
      )}

      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <SummaryMetric label="Total Produk" value={summary.totalProducts} />
        <SummaryMetric label="Valid" value={summary.validRows} />
        <SummaryMetric label="Invalid" value={summary.invalidRows} />
        <SummaryMetric label="Produk Loss" value={summary.lossProducts} />
        <SummaryMetric label="Produk Thin" value={summary.thinProducts} />
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryMetric label="Gross Sales" value={formatCurrency(summary.totalGrossSales)} />
        <SummaryMetric label="Total Fee" value={formatCurrency(summary.totalMarketplaceFees)} />
        <SummaryMetric label="Net Payout" value={formatCurrency(summary.totalNetPayout)} />
        <SummaryMetric label="Net Profit" value={formatCurrency(summary.totalNetProfit)} />
      </div>

      <div className="mt-3 max-w-xs">
        <SummaryMetric label="Average Margin" value={formatPercent(summary.averageMargin)} />
      </div>

      <div className="mt-6">
        <ProductTable products={products} onLoadProduct={onLoadProduct} />
      </div>

      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50">
          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="h-6 w-6 animate-spin rounded-full border-4 border-emerald-200 border-t-emerald-700"></div>
              <p className="text-sm font-semibold text-slate-950">Memproses CSV...</p>
            </div>
          </div>
        </div>
      )}

      {showClearConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50">
          <div className="mx-4 w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-950">Konfirmasi Hapus</h3>
            <p className="mt-2 text-sm text-slate-600">
              Hapus semua produk impor yang tersimpan di browser ini? Tindakan ini tidak dapat dibatalkan.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={confirmClear}
                className="flex-1 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-700"
              >
                Hapus
              </button>
              <button
                type="button"
                onClick={cancelClear}
                className="flex-1 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
